// src/screens/GiftCardScreen.js
// ============================================================================
// GIFT CARD SCREEN (Crit-denominated — pivot update 2026-07-09)
//
// A gift card is a Crit pack for someone else:
// - Pick a pack, pay by card (embedded Stripe checkout, same as Crit packs)
// - The code is emailed to you + shown in History; you share it
// - Redeeming a code adds the Crits to the recipient's balance
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Modal,
  Pressable,
  Clipboard,
  Platform,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import useCritsEnabled from '../hooks/useCritsEnabled';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#888',
  DANGER: '#E74C3C',
  BLUE: '#3498DB',
  PURPLE: '#9B59B6',
  GIFT: '#E91E63', // Pink accent for gift cards
};

const IS_WEB = Platform.OS === 'web';

const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((cents || 0) / 100);
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function GiftCardScreen({ navigation }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled — gift cards are Crit-denominated

  // Balance state — Crits are what gift cards deliver now; giftBalanceCents is
  // legacy display only (pre-pivot cash gift balance, shown while non-zero).
  const [critBalance, setCritBalance] = useState(0);
  const [giftBalanceCents, setGiftBalanceCents] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState('purchase'); // 'purchase', 'redeem', 'history'

  // Purchase state — pack picker + embedded Stripe checkout
  const [packs, setPacks] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey }
  const checkoutContainerRef = useRef(null);
  const checkoutInstanceRef = useRef(null);
  const pendingPackRef = useRef(null); // pack snapshot for the success modal

  // Redeem state
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);

  // History state
  const [purchasedCards, setPurchasedCards] = useState([]);
  const [redeemedCards, setRedeemedCards] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Subscribe to user's balances
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user?.uid) {
      setBalanceCents(0);
      setGiftBalanceCents(0);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      const data = snap.data() || {};
      setCritBalance(Math.max(0, Math.floor(Number(data.critBalance || 0))));
      setGiftBalanceCents(Number(data.giftBalanceCents || 0));
      setBalanceLoading(false);
    }, (error) => {
      console.error('Error fetching balance:', error);
      setBalanceLoading(false);
    });

    return unsubscribe;
  }, []);

  // Load the gift-card pack catalog (the Crit packs, priced like the
  // buy-for-yourself flow).
  const fetchPacks = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getGiftCardOptions');
      const result = await fn();
      const list = result.data?.packs || [];
      setPacks(list);
      setSelectedPackId((prev) => prev || list[0]?.id || null);
    } catch (err) {
      console.warn('[GiftCardScreen] pack load failed:', err.message);
    }
  }, [functions]);

  // Fetch gift card history
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const [purchasedResult, redeemedResult] = await Promise.all([
        httpsCallable(functions, 'getMyPurchasedGiftCards')({ limit: 20 }),
        httpsCallable(functions, 'getMyRedeemedGiftCards')({ limit: 20 }),
      ]);
      setPurchasedCards(purchasedResult.data.giftCards || []);
      setRedeemedCards(redeemedResult.data.giftCards || []);
    } catch (err) {
      console.error('Error fetching gift card history:', err);
    } finally {
      setHistoryLoading(false);
      setRefreshing(false);
    }
  }, [functions]);

  useEffect(() => {
    if (isFocused) {
      fetchHistory();
      fetchPacks();
    }
  }, [isFocused, fetchHistory, fetchPacks]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchHistory();
  };

  // ============================================================================
  // PURCHASE HANDLERS
  // ============================================================================

  // Buy the selected pack as a gift: create the checkout session and open the
  // on-screen embedded Stripe checkout (same pattern as SubscriptionScreen).
  // The code is minted by the webhook after payment and emailed to the buyer.
  const handlePurchasePress = async () => {
    const pack = packs.find((p) => p.id === selectedPackId);
    if (!pack) {
      showError('Pick a gift card size first');
      return;
    }

    // Validate email if provided
    if (recipientEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    try {
      setPurchasing(true);
      const createCheckout = httpsCallable(functions, 'createGiftCardCheckout');
      const result = await createCheckout({
        packId: pack.id,
        recipientEmail: recipientEmail || undefined,
        message: giftMessage || undefined,
      });

      pendingPackRef.current = pack;
      if (IS_WEB && result.data?.clientSecret) {
        setEmbedded({
          clientSecret: result.data.clientSecret,
          publishableKey: result.data.publishableKey,
        });
      } else {
        throw new Error('Could not start checkout.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      showError('Could not start checkout', err.message || 'Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const closeEmbedded = useCallback(() => {
    try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
    checkoutInstanceRef.current = null;
    setEmbedded(null);
  }, []);

  // Mount the embedded checkout whenever we have a clientSecret + a container.
  useEffect(() => {
    if (!IS_WEB || !embedded?.clientSecret) return;
    let cancelled = false;
    (async () => {
      try {
        if (typeof embedded.publishableKey !== 'string' || !embedded.publishableKey) {
          throw new Error('Stripe publishable key is not configured.');
        }
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(embedded.publishableKey);
        if (cancelled || !stripe) return;
        const instance = await stripe.createEmbeddedCheckoutPage({
          clientSecret: embedded.clientSecret,
          onComplete: () => {
            // Payment captured — the webhook mints + emails the code async.
            const pack = pendingPackRef.current;
            pendingPackRef.current = null;
            try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
            checkoutInstanceRef.current = null;
            setEmbedded(null);
            setPurchaseResult({ purchased: true, crits: pack?.crits || null });
            setRecipientEmail('');
            setGiftMessage('');
            // Give the webhook a moment, then refresh so the code shows in History.
            setTimeout(fetchHistory, 2500);
            setTimeout(fetchHistory, 8000);
          },
        });
        if (cancelled) { try { instance.destroy(); } catch (e) { /* noop */ } return; }
        checkoutInstanceRef.current = instance;
        if (checkoutContainerRef.current) instance.mount(checkoutContainerRef.current);
      } catch (err) {
        console.error('[GiftCardScreen] embedded checkout mount failed:', err);
        if (!cancelled) {
          setEmbedded(null);
          showError('Could not open checkout', 'Please try again.');
        }
      }
    })();
    return () => {
      cancelled = true;
      try { checkoutInstanceRef.current?.destroy(); } catch (e) { /* noop */ }
      checkoutInstanceRef.current = null;
    };
  }, [embedded, fetchHistory, showError]);

  // ============================================================================
  // REDEEM HANDLERS
  // ============================================================================

  const formatCodeInput = (text) => {
    // Remove all non-alphanumeric characters
    const cleaned = text.toUpperCase().replace(/[^A-Z0-9]/g, '');
    
    // Format as CRIT-XXXX-XXXX-XXXX
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.substring(0, 4);
      if (cleaned.length > 4) {
        formatted += '-' + cleaned.substring(4, 8);
      }
      if (cleaned.length > 8) {
        formatted += '-' + cleaned.substring(8, 12);
      }
      if (cleaned.length > 12) {
        formatted += '-' + cleaned.substring(12, 16);
      }
    }
    return formatted;
  };

  const handleCodeChange = (text) => {
    setRedeemCode(formatCodeInput(text));
  };

  const handleRedeem = async () => {
    if (!redeemCode || redeemCode.length < 19) {
      showError('Please enter a valid gift card code');
      return;
    }

    try {
      setRedeeming(true);
      const redeemGiftCard = httpsCallable(functions, 'redeemGiftCard');
      const result = await redeemGiftCard({ code: redeemCode });

      setRedeemResult(result.data);
      setRedeemCode('');
      // Refresh history
      fetchHistory();
    } catch (err) {
      console.error('Redeem error:', err);
      showError(err.message || 'Failed to redeem gift card');
    } finally {
      setRedeeming(false);
    }
  };

  // ============================================================================
  // COPY TO CLIPBOARD
  // ============================================================================

  const copyToClipboard = async (text) => {
    try {
      if (Platform.OS === 'web') {
        await navigator.clipboard.writeText(text);
      } else {
        await Clipboard.setString(text);
      }
      showError('Copied to clipboard!');
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderBalanceCard = () => (
    <View style={styles.balanceContainer}>
      <View style={styles.balanceCard}>
        <Text style={styles.balanceLabel}>Your Crits</Text>
        <Text style={styles.balanceAmount}>
          {balanceLoading ? (
            <ActivityIndicator color={COLORS.GOLD} size="small" />
          ) : (
            critBalance.toLocaleString('en-US')
          )}
        </Text>
        <Text style={styles.balanceSubtext}>Redeemed gift cards land here</Text>
      </View>

      {/* Legacy pre-pivot cash gift balance — display only while non-zero */}
      {giftBalanceCents > 0 && (
        <View style={[styles.balanceCard, styles.giftBalanceCard]}>
          <Text style={styles.balanceLabel}>Legacy Gift Balance</Text>
          <Text style={[styles.balanceAmount, styles.giftBalanceAmount]}>
            {formatCurrency(giftBalanceCents)}
          </Text>
          <Text style={styles.balanceSubtext}>From earlier gift cards</Text>
        </View>
      )}
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabContainer}>
      {['purchase', 'redeem', 'history'].map((tab) => (
        <TouchableOpacity
          key={tab}
          style={[styles.tab, activeTab === tab && styles.tabActive]}
          onPress={() => setActiveTab(tab)}
        >
          <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>
            {tab === 'purchase' ? 'Buy' : tab === 'redeem' ? 'Redeem' : 'History'}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderPurchaseTab = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Buy a Gift Card</Text>
      <Text style={styles.sectionSubtitle}>
        Gift Crits to a friend — they redeem the code and jump straight into events.
      </Text>

      {/* Pack Picker */}
      {packs.length === 0 ? (
        <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 20 }} />
      ) : (
        <View style={styles.packGrid}>
          {packs.map((pack) => {
            const selected = pack.id === selectedPackId;
            return (
              <TouchableOpacity
                key={pack.id}
                style={[styles.packCard, selected && styles.packCardActive]}
                onPress={() => setSelectedPackId(pack.id)}
              >
                <Text style={[styles.packCrits, selected && styles.packCritsActive]}>
                  {pack.crits}
                </Text>
                <Text style={styles.packCritsLabel}>Crits</Text>
                <Text style={styles.packName}>{pack.name}</Text>
                <Text style={styles.packPrice}>{formatCurrency(pack.priceCents)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {/* Optional: Recipient Email */}
      <Text style={styles.fieldLabel}>Recipient Email (optional)</Text>
      <TextInput
        style={styles.textInput}
        value={recipientEmail}
        onChangeText={setRecipientEmail}
        placeholder="friend@example.com"
        placeholderTextColor={COLORS.DIM}
        keyboardType="email-address"
        autoCapitalize="none"
      />
      <Text style={styles.inputHint}>
        We'll email you the code. You share it with them!
      </Text>

      {/* Optional: Message */}
      <Text style={styles.fieldLabel}>Personal Message (optional)</Text>
      <TextInput
        style={[styles.textInput, styles.messageInput]}
        value={giftMessage}
        onChangeText={setGiftMessage}
        placeholder="Happy gaming! 🎮"
        placeholderTextColor={COLORS.DIM}
        multiline
        maxLength={500}
      />

      {/* Purchase Button */}
      <TouchableOpacity
        style={[
          styles.actionButton,
          styles.purchaseButton,
          (purchasing || !selectedPackId) && styles.actionButtonDisabled,
        ]}
        onPress={handlePurchasePress}
        disabled={purchasing || !selectedPackId}
      >
        {purchasing ? (
          <ActivityIndicator color="#000" />
        ) : (
          <Text style={styles.actionButtonText}>
            Buy Gift Card
          </Text>
        )}
      </TouchableOpacity>
      <Text style={styles.inputHint}>
        Secure card payment via Stripe. The code is emailed to you to share.
      </Text>
    </View>
  );

  const renderRedeemTab = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Redeem Gift Card</Text>
      <Text style={styles.sectionSubtitle}>
        Enter your gift card code to add the Crits to your account.
      </Text>

      {/* Code Input */}
      <Text style={styles.fieldLabel}>Gift Card Code</Text>
      <TextInput
        style={[styles.textInput, styles.codeInput]}
        value={redeemCode}
        onChangeText={handleCodeChange}
        placeholder="CRIT-XXXX-XXXX-XXXX"
        placeholderTextColor={COLORS.DIM}
        autoCapitalize="characters"
        maxLength={19}
      />

      {/* Redeem Button */}
      <TouchableOpacity
        style={[
          styles.actionButton,
          styles.redeemButton,
          (redeeming || redeemCode.length < 19) && styles.actionButtonDisabled,
        ]}
        onPress={handleRedeem}
        disabled={redeeming || redeemCode.length < 19}
      >
        {redeeming ? (
          <ActivityIndicator color="#000" />
        ) : (
          <Text style={styles.actionButtonText}>
            Redeem Code
          </Text>
        )}
      </TouchableOpacity>

      {/* Info Box */}
      <View style={styles.infoBox}>
        <Text style={styles.infoTitle}>ℹ️ About Gift Cards</Text>
        <Text style={styles.infoText}>
          • Redeeming adds the card's Crits straight to your balance
        </Text>
        <Text style={styles.infoText}>
          • Crits are how you enter events on CritCash
        </Text>
        <Text style={styles.infoText}>
          • Gift card codes never expire
        </Text>
      </View>
    </View>
  );

  const renderHistoryTab = () => (
    <View style={styles.section}>
      {/* Purchased Cards */}
      <Text style={styles.sectionTitle}>Purchased Gift Cards</Text>
      {historyLoading ? (
        <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 20 }} />
      ) : purchasedCards.length === 0 ? (
        <Text style={styles.emptyText}>No gift cards purchased yet</Text>
      ) : (
        purchasedCards.map((card) => (
          <View key={card.id} style={styles.historyCard}>
            <View style={styles.historyHeader}>
              <Text style={styles.historyAmount}>
                {card.crits ? `${card.crits} Crits` : formatCurrency(card.amountCents)}
              </Text>
              <View style={[
                styles.statusBadge,
                card.status === 'redeemed' && styles.statusRedeemed,
                card.status === 'active' && styles.statusActive,
              ]}>
                <Text style={styles.statusText}>
                  {card.status === 'redeemed' ? 'Redeemed'
                    : card.status === 'pending_payment' ? 'Processing'
                    : card.status === 'refunded' ? 'Refunded'
                    : 'Active'}
                </Text>
              </View>
            </View>

            {card.formattedCode ? (
              <TouchableOpacity
                style={styles.codeRow}
                onPress={() => copyToClipboard(card.formattedCode)}
              >
                <Text style={styles.codeText}>{card.formattedCode}</Text>
                <Text style={styles.copyHint}>Tap to copy</Text>
              </TouchableOpacity>
            ) : null}
            
            <Text style={styles.historyDate}>
              Purchased {formatDateTime(card.createdAt)}
            </Text>
            
            {card.redeemedByName && (
              <Text style={styles.historyDetail}>
                Redeemed by {card.redeemedByName}
              </Text>
            )}
          </View>
        ))
      )}

      {/* Redeemed Cards */}
      <Text style={[styles.sectionTitle, { marginTop: 24 }]}>Redeemed Gift Cards</Text>
      {historyLoading ? (
        <ActivityIndicator color={COLORS.GIFT} style={{ marginVertical: 20 }} />
      ) : redeemedCards.length === 0 ? (
        <Text style={styles.emptyText}>No gift cards redeemed yet</Text>
      ) : (
        redeemedCards.map((card) => (
          <View key={card.id} style={[styles.historyCard, styles.redeemedCard]}>
            <View style={styles.historyHeader}>
              <Text style={[styles.historyAmount, styles.giftAmount]}>
                {card.crits ? `+${card.crits} Crits` : `+${formatCurrency(card.amountCents)}`}
              </Text>
            </View>
            
            <Text style={styles.historyDate}>
              Redeemed {formatDateTime(card.redeemedAt)}
            </Text>
            
            {(card.fromName || card.purchaserName) && (
              <Text style={styles.historyDetail}>
                From {card.fromName || card.purchaserName}
              </Text>
            )}
            
            {card.message && (
              <View style={styles.messageBox}>
                <Text style={styles.messageText}>"{card.message}"</Text>
              </View>
            )}
          </View>
        ))
      )}
    </View>
  );

  // ============================================================================
  // MODALS
  // ============================================================================

  const renderSuccessModal = () => {
    if (!purchaseResult && !redeemResult) return null;

    const isPurchase = !!purchaseResult;
    const result = purchaseResult || redeemResult;
    const critCount = result.crits || null;

    return (
      <Modal visible={!!result} transparent animationType="fade">
        <Pressable
          style={styles.modalBackdrop}
          onPress={() => {
            setPurchaseResult(null);
            setRedeemResult(null);
          }}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>
              {isPurchase ? '🎉 Gift Card Purchased!' : '✅ Code Redeemed!'}
            </Text>

            <View style={[styles.modalAmountBox, !isPurchase && styles.giftAmountBox]}>
              <Text style={styles.modalAmountLabel}>
                {isPurchase ? 'Gift Card Value' : 'Added to Your Crits'}
              </Text>
              <Text style={[styles.modalAmount, !isPurchase && styles.giftModalAmount]}>
                {critCount ? `${!isPurchase ? '+' : ''}${critCount} Crits` : formatCurrency(result.amountCents)}
              </Text>
            </View>

            {isPurchase && (
              <Text style={styles.modalNote}>
                Your gift card code is on its way to your email — it also appears
                under History in a moment. Share it with your favorite competitor!
              </Text>
            )}

            {!isPurchase && (
              <Text style={styles.modalNote}>
                {result.fromName ? `From ${result.fromName}. ` : ''}The Crits are in
                your balance — use them to enter events!
              </Text>
            )}

            {!isPurchase && result.message ? (
              <View style={styles.messageBox}>
                <Text style={styles.messageText}>"{result.message}"</Text>
              </View>
            ) : null}

            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => {
                setPurchaseResult(null);
                setRedeemResult(null);
              }}
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.GOLD}
          />
        }
      >
        <Text style={styles.screenTitle}>🎁 Gift Cards</Text>

        {/* Gift cards are Crit-denominated: when the Crit economy flag is off
            the whole surface collapses to a plain not-available line. Gated at
            the render (not above) so the hooks above always run. */}
        {critsEnabled ? (
          <>
            {renderBalanceCard()}
            {renderTabs()}

            {activeTab === 'purchase' && renderPurchaseTab()}
            {activeTab === 'redeem' && renderRedeemTab()}
            {activeTab === 'history' && renderHistoryTab()}
          </>
        ) : (
          <Text style={styles.sectionSubtitle}>Gift cards are not available right now.</Text>
        )}

        {/* Back Button */}
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Text style={styles.backButtonText}>← Back to Wallet</Text>
        </TouchableOpacity>
      </ScrollView>

      {renderSuccessModal()}

      {/* On-screen Stripe embedded checkout — keeps the user on this screen
          (no redirect), same pattern as SubscriptionScreen. */}
      {IS_WEB && embedded && (
        <Modal visible transparent animationType="fade" onRequestClose={closeEmbedded}>
          <View style={styles.checkoutOverlay}>
            <Pressable style={styles.checkoutScrim} onPress={closeEmbedded} />
            <View style={styles.checkoutCard}>
              <View style={styles.checkoutHeader}>
                <Text style={styles.checkoutTitle}>Buy Gift Card</Text>
                <Pressable onPress={closeEmbedded} hitSlop={8}>
                  <Text style={styles.checkoutClose}>✕</Text>
                </Pressable>
              </View>
              <ScrollView style={styles.checkoutScroll} showsVerticalScrollIndicator={false}>
                {/* RN Web renders raw DOM nodes; Stripe mounts the embedded
                    checkout iframe into this div. */}
                <div ref={checkoutContainerRef} style={{ width: '100%', minHeight: 360 }} />
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  // Pack picker (Crit gift cards)
  packGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  packCard: {
    flexGrow: 1,
    flexBasis: '45%',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER,
    borderWidth: 2,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 8,
  },
  packCardActive: {
    borderColor: COLORS.GOLD,
    backgroundColor: '#171307',
  },
  packCrits: {
    color: COLORS.TEXT,
    fontSize: 28,
    fontWeight: '900',
  },
  packCritsActive: {
    color: COLORS.GOLD,
  },
  packCritsLabel: {
    color: COLORS.DIM,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
  },
  packName: {
    color: COLORS.TEXT,
    fontSize: 13,
    fontWeight: '700',
  },
  packPrice: {
    color: COLORS.DIM,
    fontSize: 13,
    marginTop: 2,
  },

  // Embedded Stripe checkout overlay
  checkoutOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 },
  checkoutScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  checkoutCard: { width: '100%', maxWidth: 480, maxHeight: '88%', backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden' },
  checkoutHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#0c0c0c' },
  checkoutTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  checkoutClose: { color: '#fff', fontSize: 18, fontWeight: '700' },
  checkoutScroll: { backgroundColor: '#fff' },

  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 100,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 20,
  },

  // Balance Cards
  balanceContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  balanceCard: {
    flex: 1,
    backgroundColor: COLORS.CARD,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  giftBalanceCard: {
    borderColor: COLORS.GIFT,
    borderWidth: 1,
  },
  balanceLabel: {
    fontSize: 12,
    color: COLORS.DIM,
    marginBottom: 4,
  },
  balanceAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: COLORS.GOLD,
  },
  giftBalanceAmount: {
    color: COLORS.GIFT,
  },
  balanceSubtext: {
    fontSize: 11,
    color: COLORS.DIM,
    marginTop: 4,
  },

  // Tabs
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 4,
    marginBottom: 20,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 10,
  },
  tabActive: {
    backgroundColor: COLORS.GOLD,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.DIM,
  },
  tabTextActive: {
    color: '#000',
  },

  // Section
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.TEXT,
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: COLORS.DIM,
    marginBottom: 16,
    lineHeight: 20,
  },

  // Quick Amounts
  quickAmounts: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  quickAmountBtn: {
    flex: 1,
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    paddingVertical: 14,
    marginHorizontal: 4,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  quickAmountBtnActive: {
    backgroundColor: COLORS.GOLD,
    borderColor: COLORS.GOLD,
  },
  quickAmountText: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.TEXT,
  },
  quickAmountTextActive: {
    color: '#000',
  },

  // Amount Input
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  currencySymbol: {
    fontSize: 28,
    color: COLORS.DIM,
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 36,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    paddingVertical: 16,
  },
  inputHint: {
    fontSize: 12,
    color: COLORS.DIM,
    marginBottom: 16,
    paddingHorizontal: 4,
  },

  // Text Inputs
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.TEXT,
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: COLORS.TEXT,
    marginBottom: 8,
  },
  messageInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  codeInput: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 18,
    letterSpacing: 2,
    textAlign: 'center',
  },

  // Action Buttons
  actionButton: {
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
    marginTop: 16,
  },
  purchaseButton: {
    backgroundColor: COLORS.GOLD,
  },
  redeemButton: {
    backgroundColor: COLORS.GIFT,
  },
  actionButtonDisabled: {
    backgroundColor: '#333',
  },
  actionButtonText: {
    color: '#000',
    fontSize: 18,
    fontWeight: 'bold',
  },

  // Info Box
  infoBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 16,
    marginTop: 24,
    borderWidth: 1,
    borderColor: '#2a2a4e',
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.TEXT,
    marginBottom: 12,
  },
  infoText: {
    fontSize: 13,
    color: COLORS.DIM,
    marginBottom: 6,
    lineHeight: 18,
  },

  // History Cards
  historyCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  redeemedCard: {
    borderColor: COLORS.GIFT,
    borderLeftWidth: 3,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  historyAmount: {
    fontSize: 20,
    fontWeight: 'bold',
    color: COLORS.GOLD,
  },
  giftAmount: {
    color: COLORS.GIFT,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: '#333',
  },
  statusActive: {
    backgroundColor: '#1a3a1a',
  },
  statusRedeemed: {
    backgroundColor: '#2a1a3a',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.TEXT,
  },
  codeRow: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  codeText: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 14,
    color: COLORS.GOLD,
    textAlign: 'center',
  },
  copyHint: {
    fontSize: 10,
    color: COLORS.DIM,
    textAlign: 'center',
    marginTop: 4,
  },
  historyDate: {
    fontSize: 12,
    color: COLORS.DIM,
  },
  historyDetail: {
    fontSize: 12,
    color: COLORS.DIM,
    marginTop: 4,
  },
  messageBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  messageText: {
    fontSize: 13,
    color: COLORS.TEXT,
    fontStyle: 'italic',
  },
  emptyText: {
    color: COLORS.DIM,
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },

  // Back Button
  backButton: {
    padding: 16,
    alignItems: 'center',
  },
  backButtonText: {
    color: COLORS.DIM,
    fontSize: 16,
  },

  // Modals
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#111',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: '#333',
  },
  modalTitle: {
    color: COLORS.GOLD,
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 20,
  },
  modalAmountBox: {
    backgroundColor: '#1a1a1a',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 20,
  },
  giftAmountBox: {
    borderWidth: 1,
    borderColor: COLORS.GIFT,
  },
  modalAmountLabel: {
    color: COLORS.DIM,
    fontSize: 14,
    marginBottom: 4,
  },
  modalAmount: {
    color: COLORS.GREEN,
    fontSize: 32,
    fontWeight: '700',
  },
  giftModalAmount: {
    color: COLORS.GIFT,
  },
  modalInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  modalInfoLabel: {
    color: COLORS.DIM,
    fontSize: 14,
  },
  modalInfoValue: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '600',
  },
  modalInfoBox: {
    backgroundColor: '#0a1a2a',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#1a3a5a',
  },
  modalInfoTitle: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  modalInfoText: {
    color: '#aaa',
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 4,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    alignItems: 'center',
  },
  modalCancelText: {
    color: COLORS.DIM,
    fontSize: 16,
    fontWeight: '600',
  },
  modalConfirmBtn: {
    flex: 2,
    padding: 14,
    borderRadius: 10,
    backgroundColor: COLORS.GOLD,
    alignItems: 'center',
  },
  modalConfirmText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },
  modalNote: {
    color: COLORS.DIM,
    fontSize: 13,
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 18,
  },

  // Done Button (Success Modal)
  doneButton: {
    width: '100%',
    padding: 16,
    borderRadius: 12,
    backgroundColor: COLORS.GOLD,
    alignItems: 'center',
    marginTop: 8,
  },
  doneButtonText: {
    color: '#000',
    fontSize: 18,
    fontWeight: '700',
  },

  // Code Box (Success Modal)
  codeBox: {
    backgroundColor: '#1a2a1a',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.GREEN,
  },
  codeBoxLabel: {
    color: COLORS.DIM,
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 8,
  },
  codeBoxCode: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 20,
    fontWeight: 'bold',
    color: COLORS.GOLD,
    textAlign: 'center',
    letterSpacing: 1,
  },
  codeBoxHint: {
    color: COLORS.GREEN,
    fontSize: 11,
    textAlign: 'center',
    marginTop: 8,
  },
});